import type { StrategyBlockInstruction } from '@segmently/schema-models/strategy';
import {
  StrategyBlockWriteValidationError,
  validateStrategyBlockWrite,
} from './strategyBlockValidation';

export const BLOCK_SUBTYPE_LIMIT = 30;
export const BLOCK_SUBTYPE_PLAYBOOK_MAX_LENGTH = 8_000;
export const BLOCK_SUBTYPE_DEFAULT_INSTRUCTION_LIMIT = 40;
export const BLOCK_SUBTYPE_NAME_MAX_LENGTH = 160;
export const BLOCK_SUBTYPE_DESCRIPTION_MAX_LENGTH = 1_000;
export const BLOCK_SUBTYPE_SLUG_MAX_LENGTH = 80;
export const BLOCK_SUBTYPE_SCREEN_EXAMPLE_LIMIT = 10;
export const BLOCK_SUBTYPE_SCREEN_EXAMPLE_MAX_LENGTH = 2_000;

const BLOCK_SUBTYPE_SLUG = /^[a-z0-9][a-z0-9_-]*$/;

export function isValidBlockSubtypeSlug(value: unknown): value is string {
  return typeof value === 'string'
    && value === value.trim()
    && value.length > 0
    && value.length <= BLOCK_SUBTYPE_SLUG_MAX_LENGTH
    && BLOCK_SUBTYPE_SLUG.test(value);
}

/**
 * C6' user-facing screen example authored inside a subtype. The level is the
 * binding strictness the tactician receives (mustHave → strict-template,
 * should → pattern, niceToHave → inspiration).
 */
export const BLOCK_SUBTYPE_SCREEN_EXAMPLE_LEVELS = ['mustHave', 'should', 'niceToHave'] as const;

export type BlockSubtypeScreenExampleLevel = typeof BLOCK_SUBTYPE_SCREEN_EXAMPLE_LEVELS[number];

export interface BlockSubtypeScreenExample {
  level: BlockSubtypeScreenExampleLevel;
  text: string;
}

export interface BlockSubtypeInput {
  name: string;
  slug: string;
  description: string;
  playbook: string;
  defaultInstructions: StrategyBlockInstruction[];
  /** C6': project-local screen examples for the tactician; absent on legacy docs = []. */
  screenExamples: BlockSubtypeScreenExample[];
  enabled: boolean;
}

export interface BlockSubtype extends BlockSubtypeInput {
  id: string;
  usageCount?: number;
  createdBy?: string | null;
  createdAt?: Date | string | null;
  updatedBy?: string | null;
  updatedAt?: Date | string | null;
}

export class BlockSubtypeValidationError extends Error {
  readonly code = 'invalid_block_subtype';

  constructor(readonly field: string, message: string) {
    super(message);
    this.name = 'BlockSubtypeValidationError';
  }
}

/** Normalize and validate the one canonical user/CLI catalog payload. */
export function normalizeBlockSubtypeInput(value: unknown): BlockSubtypeInput {
  if (!isRecord(value)) throw invalid('body', 'Block subtype body must be a JSON object.');

  const name = normalizedRequiredString(value.name, 'name', BLOCK_SUBTYPE_NAME_MAX_LENGTH);
  const slug = exactSlug(value.slug);
  const description = normalizedOptionalString(
    value.description,
    'description',
    BLOCK_SUBTYPE_DESCRIPTION_MAX_LENGTH,
  );
  const playbook = requiredBoundedString(
    value.playbook,
    'playbook',
    BLOCK_SUBTYPE_PLAYBOOK_MAX_LENGTH,
  );
  if (!Array.isArray(value.defaultInstructions)) {
    throw invalid('defaultInstructions', 'defaultInstructions must be an array.');
  }
  if (value.defaultInstructions.length > BLOCK_SUBTYPE_DEFAULT_INSTRUCTION_LIMIT) {
    throw invalid(
      'defaultInstructions',
      `defaultInstructions must contain at most ${BLOCK_SUBTYPE_DEFAULT_INSTRUCTION_LIMIT} entries.`,
    );
  }
  try {
    validateStrategyBlockWrite({
      blockType: 'custom',
      instructions: value.defaultInstructions as StrategyBlockInstruction[],
    });
  } catch (error) {
    if (error instanceof StrategyBlockWriteValidationError) {
      throw invalid('defaultInstructions', error.message);
    }
    throw error;
  }
  const screenExamples = normalizeScreenExamples(value.screenExamples);
  if (typeof value.enabled !== 'boolean') {
    throw invalid('enabled', 'enabled must be a boolean.');
  }

  return {
    name,
    slug,
    description,
    playbook,
    defaultInstructions: value.defaultInstructions as StrategyBlockInstruction[],
    screenExamples,
    enabled: value.enabled,
  };
}

/** Absent on legacy docs → []. Bodies keep internal newlines (multiline by design). */
function normalizeScreenExamples(value: unknown): BlockSubtypeScreenExample[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    throw invalid('screenExamples', 'screenExamples must be an array.');
  }
  if (value.length > BLOCK_SUBTYPE_SCREEN_EXAMPLE_LIMIT) {
    throw invalid(
      'screenExamples',
      `screenExamples must contain at most ${BLOCK_SUBTYPE_SCREEN_EXAMPLE_LIMIT} entries.`,
    );
  }
  return value.map((entry, index) => {
    if (!isRecord(entry)) {
      throw invalid('screenExamples', `screenExamples[${index}] must be an object.`);
    }
    const level = entry.level;
    if (!BLOCK_SUBTYPE_SCREEN_EXAMPLE_LEVELS.includes(level as BlockSubtypeScreenExampleLevel)) {
      throw invalid(
        'screenExamples',
        `screenExamples[${index}].level must be one of: ${BLOCK_SUBTYPE_SCREEN_EXAMPLE_LEVELS.join(', ')}.`,
      );
    }
    if (typeof entry.text !== 'string' || !entry.text.trim()) {
      throw invalid('screenExamples', `screenExamples[${index}].text is required.`);
    }
    const text = entry.text.trim();
    if (text.length > BLOCK_SUBTYPE_SCREEN_EXAMPLE_MAX_LENGTH) {
      throw invalid(
        'screenExamples',
        `screenExamples[${index}].text must be at most ${BLOCK_SUBTYPE_SCREEN_EXAMPLE_MAX_LENGTH} characters.`,
      );
    }
    return { level: level as BlockSubtypeScreenExampleLevel, text };
  });
}

function exactSlug(value: unknown): string {
  if (typeof value !== 'string'
    || !value
    || value !== value.trim()
    || value.length > BLOCK_SUBTYPE_SLUG_MAX_LENGTH
    || !BLOCK_SUBTYPE_SLUG.test(value)) {
    throw invalid(
      'slug',
      `slug must be a normalized lowercase slug of at most ${BLOCK_SUBTYPE_SLUG_MAX_LENGTH} characters.`,
    );
  }
  return value;
}

function normalizedRequiredString(value: unknown, field: string, limit: number): string {
  if (typeof value !== 'string' || !value.trim()) throw invalid(field, `${field} is required.`);
  const normalized = value.trim();
  if (normalized.length > limit) throw invalid(field, `${field} must be at most ${limit} characters.`);
  return normalized;
}

function normalizedOptionalString(value: unknown, field: string, limit: number): string {
  if (value === undefined) return '';
  if (typeof value !== 'string') throw invalid(field, `${field} must be a string.`);
  const normalized = value.trim();
  if (normalized.length > limit) throw invalid(field, `${field} must be at most ${limit} characters.`);
  return normalized;
}

function requiredBoundedString(value: unknown, field: string, limit: number): string {
  if (typeof value !== 'string') throw invalid(field, `${field} must be a string.`);
  if (value.length > limit) throw invalid(field, `${field} must be at most ${limit} characters.`);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function invalid(field: string, message: string): BlockSubtypeValidationError {
  return new BlockSubtypeValidationError(field, message);
}
